import React from 'react';
import { Box, Typography, Button, Container, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Refresh as RefreshIcon, Home as HomeIcon } from '@material-ui/icons';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(4),
    textAlign: 'center',
    maxWidth: 600,
    borderRadius: theme.spacing(2),
  },
  icon: {
    fontSize: '4rem',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  errorDetails: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    textAlign: 'left',
    overflow: 'auto',
    maxHeight: 200,
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(3),
  },
  button: {
    minWidth: 120,
  },
}));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Check if it's a destructuring error
    if (error.message && error.message.includes('destructured')) {
      console.error('Destructuring error detected:', error.message);
    }
  }

  handleReload = () => {
    // Clear error state and reload
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear error state and go to home
    this.setState({ hasError: false, error: null, errorInfo: null });

    // Clear localStorage to reset authentication state
    localStorage.clear();

    // Redirect to login
    window.location.href = '/login';
  };

  toggleDetails = () => {
    this.setState(prevState => ({ showDetails: !prevState.showDetails }));
  };

  render() {
    const { hasError, error, errorInfo, showDetails } = this.state;
    const { children } = this.props;

    if (hasError) {
      return <ErrorFallback
        error={error}
        errorInfo={errorInfo}
        showDetails={showDetails}
        onReload={this.handleReload}
        onGoHome={this.handleGoHome}
        onToggleDetails={this.toggleDetails}
      />;
    }

    return children;
  }
}

const ErrorFallback = ({
  error,
  errorInfo,
  showDetails,
  onReload,
  onGoHome,
  onToggleDetails
}) => {
  const classes = useStyles();

  const isDestructuringError = error?.message?.includes('destructured') ||
                               error?.message?.includes('Cannot read properties');

  // Função helper para obter tradução com fallback
  const getTranslation = (key, fallback) => {
    try {
      const translation = i18n.t(key);
      return translation !== key ? translation : fallback;
    } catch (e) {
      return fallback;
    }
  };

  return (
    <Box className={classes.root}>
      <Container maxWidth="sm">
        <Paper className={classes.paper} elevation={3}>
          <Box className={classes.icon}>
            <span role="img" aria-label="warning">⚠️</span>
          </Box>

          <Typography variant="h4" className={classes.title}>
            {getTranslation("auth.errorBoundary.title", "Ops! Algo deu errado")}
          </Typography>

          <Typography variant="body1" className={classes.description}>
            {isDestructuringError
              ? getTranslation("auth.errorBoundary.description",
                  "Detectamos um problema com o carregamento dos dados. Isso pode acontecer devido a problemas temporários de conectividade ou dados inválidos retornados do servidor.")
              : getTranslation("auth.errorBoundary.genericDescription",
                  "Ocorreu um erro inesperado na aplicação. Tente recarregar a página ou fazer login novamente.")
            }
          </Typography>

          <Box className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={onReload}
              className={classes.button}
            >
              {getTranslation("auth.errorBoundary.buttons.reload", "Recarregar")}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<HomeIcon />}
              onClick={onGoHome}
              className={classes.button}
            >
              {getTranslation("auth.errorBoundary.buttons.login", "Fazer Login")}
            </Button>
          </Box>

          {error && (
            <Box mt={2}>
              <Button
                variant="text"
                size="small"
                onClick={onToggleDetails}
                color="secondary"
              >
                {showDetails
                  ? getTranslation("auth.errorBoundary.buttons.hideDetails", "Ocultar detalhes")
                  : getTranslation("auth.errorBoundary.buttons.showDetails", "Mostrar detalhes técnicos")
                }
              </Button>

              {showDetails && (
                <Box className={classes.errorDetails}>
                  <Typography variant="caption" component="div">
                    <strong>Erro:</strong> {error.message}
                  </Typography>
                  {error.stack && (
                    <Typography variant="caption" component="div" style={{ marginTop: 8 }}>
                      <strong>Stack:</strong>
                      <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                        {error.stack}
                      </pre>
                    </Typography>
                  )}
                  {errorInfo?.componentStack && (
                    <Typography variant="caption" component="div" style={{ marginTop: 8 }}>
                      <strong>Component Stack:</strong>
                      <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                        {errorInfo.componentStack}
                      </pre>
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ErrorBoundary;